import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Mail, Shield, Building2, ArrowRight, XCircle } from "lucide-react";
import { resolveLogoUrl } from "../context/TenantContext";

/** Simple hex darken (20%) for the gradient endpoint. Falls back on the input. */
function darken(hex) {
  if (!hex || !hex.startsWith("#")) return hex || "#0F172A";
  const h = hex.replace("#", "");
  const full = h.length === 3 ? h.split("").map(c => c + c).join("") : h;
  const n = parseInt(full, 16);
  if (Number.isNaN(n)) return hex;
  const r = Math.max(0, ((n >> 16) & 0xFF) - 40);
  const g = Math.max(0, ((n >> 8) & 0xFF) - 40);
  const b = Math.max(0, (n & 0xFF) - 40);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
}

/**
 * Public invite-acceptance page.
 *
 * URL: /invite/:token
 * Flow:
 *   1. GET /api/public/invites/{token} to preview tenant + role.
 *   2. If pending, show a form (name + password). If the email already has
 *      a user account on the platform, show a note that only the password
 *      is needed if they want to use a new one, but still accept name/pw
 *      (name becomes their display name on this shop if blank elsewhere).
 *   3. POST to /api/public/invites/{token}/accept. The response returns a
 *      JWT + user payload — plug it into AuthContext and route to /.
 */
export default function InviteAcceptPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", password: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/public/invites/${token}`);
        if (!alive) return;
        setPreview(data);
      } catch (e) {
        if (!alive) return;
        setErr(e.response?.data?.detail || "Invitation not found");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (preview?.user_exists === false && !form.name.trim()) {
      toast.error("Please enter your full name");
      return;
    }
    if (!form.password || form.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/public/invites/${token}/accept`, {
        name: form.name.trim() || preview?.email?.split("@")[0] || "Member",
        password: form.password,
      });
      // Hydrate auth context
      if (setSession) {
        await setSession(data.token, data.user);
      } else {
        localStorage.setItem("wc_token", data.token);
      }
      toast.success(`Welcome to ${preview?.tenant_name || "the team"}!`);
      navigate("/");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't accept this invitation");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="wc-public-light min-h-screen bg-slate-100 flex items-center justify-center p-4"
      style={{ backgroundColor: "#F1F5F9", color: "#0F172A" }}
      data-testid="invite-accept-page"
    >
      <div className="w-full max-w-md">
        <div
          className="rounded-3xl shadow-xl overflow-hidden"
          style={{ background: "#FFFFFF", border: "1px solid rgba(15,23,42,0.08)" }}
        >
          {/* Header strip — stamped with shop branding if provided */}
          <div
            className="px-6 py-5"
            style={{
              background: preview?.tenant_accent
                ? `linear-gradient(135deg, ${preview.tenant_accent}, ${darken(preview.tenant_accent)})`
                : "linear-gradient(135deg,#0F172A,#334155)",
              color: "#F8FAFC",
            }}
          >
            <div className="flex items-center gap-3">
              {preview?.tenant_logo && (
                <img
                  src={resolveLogoUrl(preview.tenant_logo)}
                  alt={preview.tenant_name || "Shop logo"}
                  data-testid="invite-tenant-logo"
                  className="w-10 h-10 rounded-xl object-cover bg-white/10"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              )}
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] mb-1" style={{ color: "rgba(255,255,255,0.75)" }}>
                  {preview?.tenant_name || "WrapConsole"}
                </div>
                <h1 className="text-xl font-semibold" style={{ color: "#F8FAFC" }}>You're invited</h1>
              </div>
            </div>
          </div>

          <div className="p-6">
            {loading && (
              <div className="text-sm italic" style={{ color: "#64748B" }}>Loading your invitation…</div>
            )}

            {!loading && err && (
              <InviteError title="We couldn't find that invitation" message={err} />
            )}

            {!loading && preview && preview.status !== "pending" && (
              <InviteError
                title={
                  preview.status === "expired"   ? "This invitation has expired"
                : preview.status === "revoked"   ? "This invitation was revoked"
                : preview.status === "accepted"  ? "This invitation has already been accepted"
                : "This invitation is no longer valid"
                }
                message={
                  preview.status === "expired"
                    ? "Ask the person who invited you to send a new one."
                    : preview.status === "accepted"
                      ? "You can sign in with your existing password."
                      : "Please get in touch with the shop owner for a fresh invite."
                }
              />
            )}

            {!loading && preview && preview.status === "pending" && (
              <form onSubmit={submit} className="space-y-4" data-testid="invite-accept-form">
                <div
                  className="rounded-2xl p-4 space-y-2"
                  style={{ background: "#F8FAFC", border: "1px solid #E2E8F0" }}
                >
                  <div className="flex items-center gap-2 text-sm" style={{ color: "#334155" }}>
                    <Building2 className="w-4 h-4" style={{ color: "#64748B" }} />
                    <span>Shop:</span>
                    <span className="font-semibold" style={{ color: "#0F172A" }} data-testid="invite-tenant-name">
                      {preview.tenant_name || "—"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm" style={{ color: "#334155" }}>
                    <Shield className="w-4 h-4" style={{ color: "#64748B" }} />
                    <span>Role:</span>
                    <span className="font-semibold capitalize" style={{ color: "#0F172A" }} data-testid="invite-role">
                      {preview.role}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm" style={{ color: "#334155" }}>
                    <Mail className="w-4 h-4" style={{ color: "#64748B" }} />
                    <span>Email:</span>
                    <span className="font-semibold" style={{ color: "#0F172A" }} data-testid="invite-email">
                      {preview.email}
                    </span>
                  </div>
                  {preview.invited_by && (
                    <div className="text-xs pt-1" style={{ color: "#64748B" }}>Invited by {preview.invited_by}</div>
                  )}
                </div>

                {preview.user_exists ? (
                  <div
                    className="rounded-xl p-3 text-xs"
                    style={{ background: "#EFF6FF", border: "1px solid #BFDBFE", color: "#1E3A8A" }}
                  >
                    An account already exists for this email. Just enter the password you want to use for this shop and we'll link it up.
                  </div>
                ) : (
                  <label className="block">
                    <span className="text-[10px] uppercase tracking-widest block mb-1" style={{ color: "#64748B" }}>Full name</span>
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Alex Installer"
                      data-testid="invite-name-input"
                      className="w-full rounded-xl px-3 py-2.5 text-sm focus:outline-none"
                      style={{ background: "#FFFFFF", border: "1px solid #CBD5E1", color: "#0F172A" }}
                      required
                    />
                  </label>
                )}

                <label className="block">
                  <span className="text-[10px] uppercase tracking-widest block mb-1" style={{ color: "#64748B" }}>
                    {preview.user_exists ? "Password" : "Set a password"}
                  </span>
                  <input
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    placeholder="At least 6 characters"
                    data-testid="invite-password-input"
                    minLength={6}
                    className="w-full rounded-xl px-3 py-2.5 text-sm focus:outline-none"
                    style={{ background: "#FFFFFF", border: "1px solid #CBD5E1", color: "#0F172A" }}
                    required
                  />
                </label>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="invite-accept-submit"
                  className="w-full rounded-full py-3 text-sm font-semibold flex items-center justify-center gap-2 transition disabled:opacity-50"
                  style={{
                    background: preview?.tenant_accent || "#0F172A",
                    color: "#F8FAFC",
                  }}
                >
                  {submitting ? "Joining…" : <>Accept &amp; join <ArrowRight className="w-4 h-4" /></>}
                </button>

                <p className="text-[11px] text-center" style={{ color: "#64748B" }}>
                  By accepting, you'll be signed in to {preview.tenant_name || "this shop"} right away.
                </p>
              </form>
            )}
          </div>
        </div>

        <div className="text-center text-xs mt-4" style={{ color: "#64748B" }}>
          Powered by WrapConsole
        </div>
      </div>
    </div>
  );
}

function InviteError({ title, message }) {
  return (
    <div className="text-center py-6 space-y-3" data-testid="invite-error">
      <div
        className="inline-flex items-center justify-center w-12 h-12 rounded-full"
        style={{ background: "#FEF2F2", border: "1px solid #FECACA" }}
      >
        <XCircle className="w-6 h-6" style={{ color: "#EF4444" }} />
      </div>
      <h2 className="text-base font-semibold" style={{ color: "#0F172A" }}>{title}</h2>
      <p className="text-sm" style={{ color: "#475569" }}>{message}</p>
    </div>
  );
}
